import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ChartBar, CurrencyGbp, Buildings, Users, Folder, Truck, Briefcase,
  TrendUp, TrendDown, Receipt, Clock, ArrowRight, Crown, Rocket,
  Lightning, ShieldCheck
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import RecoveryTrendChart from "../components/RecoveryTrendChart";

const PLAN_COLORS_MAP = {
  starter: "#3B82F6",
  professional: "#8B5CF6",
  enterprise: "#F59E0B",
  one_time_licence: "#10B981",
};

const PLAN_LABELS = {
  starter: "Starter",
  professional: "Professional",
  enterprise: "Enterprise",
  one_time_licence: "One-Time Licence",
};

const PLAN_ICONS = { starter: Rocket, professional: Lightning, enterprise: Crown, one_time_licence: ShieldCheck };

const TX_STATUS_COLORS = {
  paid: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  initiated: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-300",
};

function KPICard({ icon: Icon, label, value, sublabel, color = "text-primary", trend }) {
  return (
    <div className="border border-border rounded-lg p-4 bg-card" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-start justify-between mb-2">
        <Icon size={20} className={color} weight="duotone" />
        {trend !== undefined && (
          <div className={`flex items-center gap-0.5 text-[10px] font-semibold ${trend >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            {trend >= 0 ? <TrendUp size={10} /> : <TrendDown size={10} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold font-[Chivo] tracking-tight">{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider mt-0.5">{label}</p>
      {sublabel && <p className="text-[10px] text-muted-foreground mt-0.5">{sublabel}</p>}
    </div>
  );
}

export default function RevenueDashboardPage() {
  const [data, setData] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/platform/analytics")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
    API.get("/platform/tenant-health")
      .then(({ data }) => setHealthData(data))
      .catch(() => {});
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Failed to load analytics</div>;

  const { tenants, revenue, signup_growth, recent_transactions, platform_usage } = data;

  // Prepare pie chart data
  const pieData = Object.entries(tenants.plan_distribution || {}).map(([plan, count]) => ({
    name: PLAN_LABELS[plan] || plan,
    value: count,
    color: PLAN_COLORS_MAP[plan] || "#6B7280",
  }));

  // Prepare signup bar chart
  const signupData = (signup_growth || []).map(s => ({
    month: s.month.split(" ")[0],
    signups: s.count,
  }));

  return (
    <div data-testid="revenue-dashboard-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-600 to-cyan-600 flex items-center justify-center">
          <ChartBar size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Revenue & Analytics</h1>
          <p className="text-xs text-muted-foreground">Platform-wide performance metrics</p>
        </div>
      </div>

      {/* Revenue KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="revenue-kpis">
        <KPICard icon={CurrencyGbp} label="Monthly Revenue" value={`£${revenue.mrr.toLocaleString()}`} sublabel="MRR" color="text-emerald-600" />
        <KPICard icon={CurrencyGbp} label="Annual Revenue" value={`£${revenue.arr.toLocaleString()}`} sublabel="ARR (projected)" color="text-blue-600" />
        <KPICard icon={Receipt} label="Total Collected" value={`£${revenue.total_revenue.toLocaleString()}`} sublabel="All-time payments" color="text-purple-600" />
        <KPICard icon={Buildings} label="Paid Tenants" value={tenants.paid} sublabel={`${tenants.trialing} trialing`} color="text-amber-600" />
      </div>

      {/* Tenant KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="tenant-kpis">
        <KPICard icon={Buildings} label="Total Tenants" value={tenants.total} color="text-indigo-500" />
        <KPICard icon={Buildings} label="Active" value={tenants.active} color="text-emerald-500" />
        <KPICard icon={Lightning} label="Active (7d)" value={tenants.active_last_7d} color="text-blue-500" />
        <KPICard icon={Buildings} label="Trialing" value={tenants.trialing} color="text-amber-500" />
        <KPICard icon={Buildings} label="Suspended" value={tenants.suspended} color="text-red-500" />
        <KPICard icon={Users} label="Total Users" value={platform_usage.total_users} color="text-cyan-500" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Signup Growth Chart */}
        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-4" data-testid="signup-growth-chart">
          <h3 className="text-sm font-bold font-[Chivo] mb-3">Tenant Signups</h3>
          <div className="h-52">
            {signupData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={signupData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                  <Tooltip
                    contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)" }}
                    labelStyle={{ fontWeight: 700 }}
                  />
                  <Bar dataKey="signups" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No signup data yet</div>
            )}
          </div>
        </div>

        {/* Plan Distribution Pie */}
        <div className="border border-border rounded-lg bg-card p-4" data-testid="plan-distribution-chart">
          <h3 className="text-sm font-bold font-[Chivo] mb-3">Plan Distribution</h3>
          <div className="h-40">
            {pieData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} strokeWidth={2}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)" }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No data</div>
            )}
          </div>
          <div className="mt-2 flex flex-wrap gap-2 justify-center">
            {pieData.map((d) => (
              <div key={d.name} className="flex items-center gap-1.5 text-[10px]">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                <span className="text-muted-foreground">{d.name} ({d.value})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <RecoveryTrendChart weeks={12} />

      {/* Revenue by Plan */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="revenue-by-plan">
        {Object.entries(revenue.revenue_by_plan || {}).map(([planId, info]) => {
          const Icon = PLAN_ICONS[planId] || Rocket;
          return (
            <div key={planId} className="border border-border rounded-lg p-4 bg-card">
              <div className="flex items-center gap-2 mb-2">
                <Icon size={16} style={{ color: PLAN_COLORS_MAP[planId] }} weight="fill" />
                <span className="text-xs font-bold font-[Chivo]">{PLAN_LABELS[planId] || planId}</span>
                <Badge className="ml-auto text-[8px]" style={{ backgroundColor: PLAN_COLORS_MAP[planId], color: "#fff" }}>
                  {info.count} tenant{info.count !== 1 ? "s" : ""}
                </Badge>
              </div>
              <p className="text-lg font-bold font-[Chivo]">
                £{info.type === "one_time" ? info.revenue?.toLocaleString() : info.monthly_revenue?.toLocaleString()}
              </p>
              <p className="text-[9px] text-muted-foreground">
                {info.type === "one_time" ? "Total licence revenue" : "Monthly revenue"}
              </p>
            </div>
          );
        })}
      </div>

      {/* Health Score Summary */}
      {healthData && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="health-scores">
          <div className="border border-border rounded-md p-3 bg-card text-center">
            <p className="text-2xl font-bold font-[Chivo]">{healthData.summary.average_score}</p>
            <p className="text-[9px] text-muted-foreground uppercase">Avg Health Score</p>
          </div>
          {Object.entries(healthData.summary.grade_distribution).map(([grade, count]) => {
            const gColors = { A: "text-emerald-500", B: "text-blue-500", C: "text-amber-500", D: "text-orange-500", F: "text-red-500" };
            return (
              <div key={grade} className="border border-border rounded-md p-3 bg-card text-center">
                <p className={`text-2xl font-bold font-[Chivo] ${gColors[grade] || ""}`}>{count}</p>
                <p className="text-[9px] text-muted-foreground uppercase">Grade {grade}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Platform Usage */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="platform-usage">
        <div className="border border-border rounded-md p-3 bg-card">
          <Users size={16} className="text-blue-500 mb-1" weight="duotone" />
          <p className="text-lg font-bold font-[Chivo]">{platform_usage.total_users}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Total Users</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Folder size={16} className="text-emerald-500 mb-1" weight="duotone" />
          <p className="text-lg font-bold font-[Chivo]">{platform_usage.total_projects}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Projects</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Truck size={16} className="text-purple-500 mb-1" weight="duotone" />
          <p className="text-lg font-bold font-[Chivo]">{platform_usage.total_suppliers}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Suppliers</p>
        </div>
        <div className="border border-border rounded-md p-3 bg-card">
          <Briefcase size={16} className="text-amber-500 mb-1" weight="duotone" />
          <p className="text-lg font-bold font-[Chivo]">{platform_usage.total_jobs}</p>
          <p className="text-[9px] text-muted-foreground uppercase tracking-wider">Jobs</p>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="border border-border rounded-lg overflow-hidden" data-testid="recent-transactions">
        <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
          <Receipt size={14} className="text-muted-foreground" />
          <h3 className="text-sm font-semibold font-[Chivo]">Recent Transactions</h3>
        </div>
        {recent_transactions.length === 0 ? (
          <p className="px-4 py-8 text-xs text-muted-foreground text-center">No transactions yet</p>
        ) : (
          <div className="divide-y divide-border">
            {recent_transactions.map((tx) => (
              <div key={tx.id} className="flex items-center justify-between px-4 py-3" data-testid={`tx-${tx.id}`}>
                <div>
                  <p className="text-xs font-semibold">
                    {PLAN_LABELS[tx.plan_id] || tx.plan_id} &mdash; {tx.billing_cycle}
                  </p>
                  <p className="text-[10px] text-muted-foreground flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(tx.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    {tx.user_email && <span>&middot; {tx.user_email}</span>}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold font-[Chivo]">£{tx.amount?.toFixed(2)}</p>
                  <Badge className={`text-[8px] ${TX_STATUS_COLORS[tx.payment_status] || ""}`}>
                    {tx.payment_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
